import { useCallback, useMemo, useRef } from "react";
import { Lock, Trash2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { queryClient } from "App";
import ACL_RELATIONSHIPS from "acl-relationships";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { TableCreateButton } from "components/BaseTableV3/TableCreateButton";
import { getNextQueryParams } from "components/helpers";
import useAuthorized from "hooks/useAuthorized";
import { useCustomTableGrid } from "hooks/useCustomTableGrid";
import { useCustomTableSchema } from "hooks/useCustomTableSchema";
import { useCustomTableRowMutations } from "hooks/useCustomTableRowMutations";
import CustomTableRowService from "services/CustomTableRow";
import {
  customTableName,
  customTableQueryKey,
  customTableRowsKey,
} from "routes/custom_tables/grid/queryKeys";
import DeleteRowsModal from "routes/custom_tables/modals/DeleteRowsModal";

const CustomTableGrid = ({ slug, header, headerIcon, lockedFilters }) => {
  const deleteModalRef = useRef(null);
  const { schema } = useCustomTableSchema(slug);

  // The schema DTO's per-table grant is authoritative (reflects post-login
  // grants); fall back to the coarse ACL only when it's absent.
  const perTableCan = schema?.current_user_can ?? null;
  const pathCanWrite = useAuthorized([ACL_RELATIONSHIPS.customTable.writeData]);
  const pathCanDelete = useAuthorized([
    ACL_RELATIONSHIPS.customTable.deleteData,
  ]);
  const canWrite = perTableCan ? perTableCan.write : pathCanWrite;
  const canDelete = perTableCan ? perTableCan.delete : pathCanDelete;
  // System row-tags step aside when the table has its own ``tags`` column.
  const supportsTags = !(schema?.columns ?? []).some(
    c => c.field_id === "tags"
  );
  const isReadOnly =
    perTableCan &&
    perTableCan.read &&
    !perTableCan.write &&
    !perTableCan.delete;

  const { lastError, clearLastError } = useCustomTableRowMutations(slug);

  const displayFieldId = schema?.display_field_id ?? null;

  const onDeleteRow = useCallback(
    row => {
      if (!row?.id) return;
      deleteModalRef.current?.open({
        slug,
        ids: [row.id],
        rowsPreview: [row],
        displayFieldId,
      });
    },
    [slug, displayFieldId]
  );

  const { columns, filters, isReady } = useCustomTableGrid(slug, {
    onDeleteRow,
  });

  // A host page (e.g. a Contact's linked-table tab) can pin the grid to one
  // record: locked params ride every fetch and their filter controls are
  // hidden so the user can't contradict the pin.
  const visibleFilters = useMemo(() => {
    if (!lockedFilters) return filters;
    return filters.filter(f => !(f.key in lockedFilters));
  }, [filters, lockedFilters]);

  const fetchRows = useCallback(
    async queryParams => {
      const next = getNextQueryParams(queryParams, columns);
      return await CustomTableRowService.list(slug, {
        ...next,
        ...(lockedFilters ?? {}),
      });
    },
    [slug, columns, lockedFilters]
  );

  const invalidateRows = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: customTableRowsKey(slug),
      }),
    [slug]
  );

  const rowDetailHref = useCallback(
    row => `/custom_tables/${slug}/${row.id}`,
    [slug]
  );

  const actionButtons = useMemo(() => {
    if (!canDelete) return null;
    return ({ selectedIds, selectedData, uncheckAll }) => {
      if (!selectedIds || selectedIds.length === 0) return null;
      return (
        <Button
          variant="destructive"
          size="sm"
          onClick={() => {
            deleteModalRef.current?.open({
              slug,
              ids: selectedIds,
              rowsPreview: selectedData,
              displayFieldId,
            });
            uncheckAll?.();
          }}
        >
          <Trash2 data-icon="inline-start" /> Delete {selectedIds.length} row
          {selectedIds.length === 1 ? "" : "s"}
        </Button>
      );
    };
  }, [slug, canDelete, displayFieldId]);

  if (!isReady) {
    return null;
  }

  const topLevelError = lastError?.genericError ?? null;

  const dismissTopLevelError = () => {
    clearLastError?.();
  };

  const addRowButton = canWrite ? (
    <TableCreateButton to={`/custom_tables/${slug}/new`}>
      Add Row
    </TableCreateButton>
  ) : null;

  return (
    <div className="tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:min-w-0">
      <DeleteRowsModal ref={deleteModalRef} />
      {topLevelError && (
        <div className="tw:mx-6 tw:mb-3" role="alert" aria-live="polite">
          <div className="tw:text-[var(--color-error)] tw:text-sm tw:py-0.5">
            {topLevelError}
            <Button
              variant="ghost"
              size="icon-xs"
              type="button"
              onClick={dismissTopLevelError}
              aria-label="Dismiss error"
            >
              <X />
            </Button>
          </div>
        </div>
      )}
      <div
        data-testid="custom-table-grid"
        className="tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:min-w-0"
      >
        <BaseTableV3
          columns={columns}
          filters={visibleFilters}
          enableSearch
          enableSettings
          header={
            <>
              {header}
              {isReadOnly && (
                <Tooltip>
                  <TooltipTrigger
                    aria-label="Read-only access"
                    className="tw:inline-flex tw:align-middle tw:cursor-help tw:border-0 tw:bg-transparent tw:text-[var(--color-text-secondary)]"
                  >
                    <Lock className="tw:size-4 tw:ml-1" />
                  </TooltipTrigger>
                  <TooltipContent>Read-only access</TooltipContent>
                </Tooltip>
              )}
            </>
          }
          headerIcon={headerIcon}
          tableName={customTableName(slug)}
          queryKey={customTableQueryKey(slug)}
          className={`custom-table-${slug}`}
          tagsDropdownParams={{ visible: canWrite && supportsTags }}
          tagsModelType={`CustomTable:${slug}`}
          customTableSlug={slug}
          createButton={addRowButton}
          actionButtons={actionButtons}
          fetchData={fetchRows}
          invalidateData={invalidateRows}
          rowDetailHref={rowDetailHref}
        />
      </div>
    </div>
  );
};

export default CustomTableGrid;
